// Small, self-contained Cards extracted from Settings.jsx as part of the
// "light split" refactor. State stays in Settings.jsx; these components
// are pure UI and take a small prop surface.

import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Minimize2, Hash, Calculator, Wand2, RotateCcw, ChevronDown, Search, Vibrate, Undo2, Cloud } from "lucide-react";
import { updateDecoyById } from "@/lib/mr/decoys";
import { hapticTap } from "@/lib/mr";

// ---------------------------------------------------------------------------
// Haptic feedback — Web Vibration API for a native "iPhone-y" tap feel.
// iOS Safari silently no-ops the API, so this is purely a boost on Android
// devices + installed PWAs on both platforms. Default ON.
// ---------------------------------------------------------------------------
export function HapticsCard({ hapticsEnabled, setHapticsEnabled }) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="haptics-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Vibrate className="w-4 h-4" style={{ color: "#e85a64" }} /> Haptic feedback
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            Subtle vibrations on lock-screen key taps, note taps and buttons —
            makes the phone feel native under the thumb. iOS Safari ignores
            it; installed PWAs and Android fire it silently. Tap the toggle
            once to feel a preview.
          </p>
        </div>
        <Switch
          checked={hapticsEnabled}
          onCheckedChange={(v) => {
            setHapticsEnabled(v);
            // Fire a preview buzz on enable so the magician can confirm
            // their hardware supports it without leaving Settings.
            if (v) {
              setTimeout(() => hapticTap(), 0);
            }
          }}
          data-testid="haptics-switch"
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Decoy reset-on-exit — restore the original title/body/preview of a
// pre-configured decoy note when the spectator taps Back, so any typing
// they did during the show never leaks into the next performance.
// ---------------------------------------------------------------------------
export function DecoyResetCard({ decoyResetOnExit, setDecoyResetOnExit }) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="decoy-reset-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Undo2 className="w-4 h-4" style={{ color: "#e85a64" }} /> Auto-restore decoys
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            When a spectator opens one of your existing decoy notes and types
            in it, the note reverts to its original content the moment they
            tap Back — so your decoys always look freshly-curated. (+) notes
            they create themselves stay untouched.
          </p>
        </div>
        <Switch
          checked={decoyResetOnExit}
          onCheckedChange={setDecoyResetOnExit}
          data-testid="decoy-reset-switch"
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// iCloud "Storage Almost Full" banner — cosmetic native-iOS looking notice
// that renders directly under the note count on /notes. Sells the disguise
// because every real iCloud user has seen this message. Default ON.
// ---------------------------------------------------------------------------
export function ICloudBannerCard({ iCloudBannerEnabled, setICloudBannerEnabled }) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="icloud-banner-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Cloud className="w-4 h-4" style={{ color: "#e85a64" }} /> iCloud storage banner
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            Shows a native-looking &ldquo;iCloud Storage Almost Full. Get More
            Storage.&rdquo; notice directly under the note count. Purely
            cosmetic — no taps do anything — but every real iPhone user has
            seen it, so it hardens the disguise. Localised automatically.
          </p>
        </div>
        <Switch
          checked={iCloudBannerEnabled}
          onCheckedChange={setICloudBannerEnabled}
          data-testid="icloud-banner-switch"
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AdvancedSection — collapsible group for secondary / tuning controls.
// Uses native <details> so there's zero extra state and full a11y.
// Keeps the first-run Settings UI quiet for new users without losing
// any power-user knobs.
// ---------------------------------------------------------------------------
export function AdvancedSection({ children, label = "Advanced", testid }) {
  return (
    <details
      className="mt-5 border-t border-white/[0.06] pt-4 group"
      data-testid={testid}
    >
      <summary className="cursor-pointer list-none flex items-center justify-between text-[11px] uppercase tracking-[0.32em] text-white/45 hover:text-white/70 transition-colors select-none">
        <span>{label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-white/30 group-open:rotate-180 transition-transform" />
      </summary>
      <div className="mt-4 space-y-5">{children}</div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Backdate timestamp
// ---------------------------------------------------------------------------
export function BackdateCard({ backdateMin, setBackdateMin }) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl mr-rise delay-4"
      data-testid="backdate-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Label
            htmlFor="backdate-input"
            className="text-white text-base sm:text-lg font-semibold flex items-center gap-2 cursor-pointer"
          >
            Backdate timestamp
          </Label>
          <p className="text-xs text-white/55 mt-1.5 leading-relaxed">
            Shift the note&rsquo;s date line into the past so it reads
            like you wrote it earlier.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Input
          id="backdate-input"
          type="number"
          inputMode="numeric"
          min={0}
          max={180}
          value={backdateMin}
          onChange={(e) => {
            const raw = parseInt(e.target.value, 10);
            const clamped = Number.isFinite(raw) ? Math.max(0, Math.min(180, raw)) : 0;
            setBackdateMin(clamped);
          }}
          className="w-24 h-10 bg-black/30 border-white/10 text-white text-base tabular-nums disabled:opacity-40"
          data-testid="backdate-input"
        />
        <span className="text-sm text-white/55">minutes ago</span>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Compact reveal — abbreviate the subliminal date/zodiac/weekday
// ---------------------------------------------------------------------------
export function CompactRevealCard({ compactReveal, setCompactReveal }) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="compact-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Minimize2 className="w-4 h-4" style={{ color: "#e85a64" }} /> Compact reveal
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            Two-letter codes for weekdays, months and zodiac signs so the
            reveal never wraps. See <span className="text-white/75">The Manual</span> for examples.
          </p>
        </div>
        <Switch
          checked={compactReveal}
          onCheckedChange={setCompactReveal}
          data-testid="compact-switch"
        />
      </div>
      {compactReveal && (
        <div
          className="mt-4 p-3 rounded-lg text-[12px] leading-relaxed font-mono"
          style={{ background: "rgba(0,0,0,0.32)", color: "rgba(214,201,140,0.85)" }}
        >
          <div className="text-white/45 text-[10px] uppercase tracking-[0.32em] mb-2">Example</div>
          <div>
            Wednesday, 28 April 1999 · Taurus
            <span className="text-white/45"> &nbsp;→&nbsp; </span>
            We, 28A99, Ta
          </div>
          <div className="mt-1.5">
            80235 | Wednesday, 28 April 1999 · Taurus | Paris
            <span className="text-white/45"> &nbsp;→&nbsp; </span>
            80235 | We, 28A99, Ta | Paris
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Phantom Peek — capture a (+) note the spectator wrote and then swipe-
// deleted, and reveal it briefly under the magician's thumb when they
// tap the "Notes" h1 during the natural phone-handover beat.
// ---------------------------------------------------------------------------
export function PhantomPeekCard({
  peekEnabled, setPeekEnabled,
  peekDurationMs, setPeekDurationMs,
  peekPassMotion, setPeekPassMotion,
  peekPassThreshold, setPeekPassThreshold,
}) {
  const requestMotionPermission = async () => {
    if (typeof window === "undefined") return;
    const M = window.DeviceMotionEvent;
    if (!M || typeof M.requestPermission !== "function") {
      // Non-iOS or older iOS — permission implicit.
      return;
    }
    try {
      const state = await M.requestPermission();
      if (state !== "granted") {
        // eslint-disable-next-line no-alert
        alert("Motion permission was not granted. Pass-motion peek will not work until it is.");
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Motion permission request failed: " + (e?.message || String(e)));
    }
  };
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="peek-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Wand2 className="w-4 h-4" style={{ color: "#e85a64" }} /> Phantom Peek
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            When the spectator writes a word in a brand-new (+) note and
            then swipe-deletes it, the contents are silently captured.
            Tap the &ldquo;Notes&rdquo; title once during the hand-back
            and the word fades up in subtle grey at the bottom of the
            screen for the duration below.
          </p>
        </div>
        <Switch
          checked={peekEnabled}
          onCheckedChange={setPeekEnabled}
          data-testid="peek-switch"
        />
      </div>
      {peekEnabled && (
        <>
          <div className="mt-4 flex items-center gap-3">
            <Label
              htmlFor="peek-duration-input"
              className="text-white/80 text-sm whitespace-nowrap"
            >
              Show for
            </Label>
            <Input
              id="peek-duration-input"
              type="number"
              inputMode="numeric"
              min={500}
              max={5000}
              step={100}
              value={peekDurationMs}
              onChange={(e) => {
                const raw = parseInt(e.target.value, 10);
                const clamped = Number.isFinite(raw) ? Math.max(500, Math.min(5000, raw)) : 2000;
                setPeekDurationMs(clamped);
              }}
              className="w-24 h-10 bg-black/30 border-white/10 text-white text-base tabular-nums"
              data-testid="peek-duration-input"
            />
            <span className="text-sm text-white/55">ms (500–5000)</span>
          </div>

          {/* Pass-motion trigger — second, hands-free trigger fired by
              one moderate acceleration burst during the natural pass-
              the-phone beat. Lower threshold = more sensitive. */}
          <AdvancedSection testid="peek-advanced">
            <div>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium">Pass-motion trigger</div>
                  <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
                    Also reveal the peek when the phone is naturally
                    handed over — one moderate motion burst is enough.
                    Useful when both hands are occupied with the prop.
                  </p>
                </div>
                <Switch
                  checked={peekPassMotion}
                  onCheckedChange={(v) => {
                    setPeekPassMotion(v);
                    // Pre-grant iOS motion permission from inside this
                    // user gesture so the listener can attach later.
                    if (v) requestMotionPermission();
                  }}
                  data-testid="peek-pass-switch"
                />
              </div>
              {peekPassMotion && (
                <div className="mt-3 flex items-center gap-3">
                  <Label
                    htmlFor="peek-pass-threshold-input"
                    className="text-white/80 text-sm whitespace-nowrap"
                  >
                    Sensitivity
                  </Label>
                  <Input
                    id="peek-pass-threshold-input"
                    type="number"
                    inputMode="numeric"
                    min={6}
                    max={18}
                    step={1}
                    value={peekPassThreshold}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value, 10);
                      const clamped = Number.isFinite(raw) ? Math.max(6, Math.min(18, raw)) : 9;
                      setPeekPassThreshold(clamped);
                    }}
                    className="w-20 h-10 bg-black/30 border-white/10 text-white text-base tabular-nums"
                    data-testid="peek-pass-threshold-input"
                  />
                  <span className="text-sm text-white/55">
                    m/s² · lower = touchier (6 hyper-sensitive, 18 vigorous)
                  </span>
                </div>
              )}
            </div>
          </AdvancedSection>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Flip Force — spectator holds the phone face-down on the notes list and
// scrolls/taps freely. The moment they flip it face-up (after a minimum
// face-down dwell), the magician's chosen target decoy is force-opened.
// ---------------------------------------------------------------------------
export function FlipForceCard({
  flipForceEnabled, setFlipForceEnabled,
  flipForceTargetId, setFlipForceTargetId,
  flipForceTargetId2, setFlipForceTargetId2,
  flipForceTargetId3, setFlipForceTargetId3,
  flipForceMinFacedownMs, setFlipForceMinFacedownMs,
  decoys, setDecoys,
}) {
  const requestMotionPermission = async () => {
    if (typeof window === "undefined") return;
    const M = window.DeviceMotionEvent;
    const O = window.DeviceOrientationEvent;
    const needsMotion = M && typeof M.requestPermission === "function";
    const needsOrient = O && typeof O.requestPermission === "function";
    if (!needsMotion && !needsOrient) return;
    try {
      // Request BOTH — Flip Force listens to devicemotion AND
      // deviceorientation, and iOS Safari treats them as separate grants.
      const results = await Promise.all([
        needsMotion ? M.requestPermission() : Promise.resolve("granted"),
        needsOrient ? O.requestPermission() : Promise.resolve("granted"),
      ]);
      if (!results.every((r) => r === "granted")) {
        // eslint-disable-next-line no-alert
        alert("Motion permission was not granted. Flip Force will not work until it is.");
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Motion permission request failed: " + (e?.message || String(e)));
    }
  };
  const safeDecoys = Array.isArray(decoys) ? decoys.filter((d) => d && d.id) : [];
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="flip-force-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <RotateCcw className="w-4 h-4" style={{ color: "#7aa2ff" }} /> Flip Force
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            Spectator holds the phone screen-down and freely scrolls /
            taps the notes list. As soon as they flip it face-up (after
            the dwell time below), the chosen target note opens
            automatically — overriding whatever they last tapped.
          </p>
        </div>
        <Switch
          checked={flipForceEnabled}
          onCheckedChange={(v) => {
            setFlipForceEnabled(v);
            if (v) requestMotionPermission();
          }}
          data-testid="flip-force-switch"
        />
      </div>
      {flipForceEnabled && (
        <>
          <div className="mt-4">
            <Label
              htmlFor="flip-force-target-select"
              className="text-white/80 text-sm"
            >
              Target note 1 (convincer)
            </Label>
            <p className="text-[11px] text-white/45 mt-0.5 mb-1 leading-relaxed">
              First force — fires on the first qualifying flip.
            </p>
            <Select
              value={flipForceTargetId || ""}
              onValueChange={(v) => setFlipForceTargetId(v)}
            >
              <SelectTrigger
                id="flip-force-target-select"
                className="mt-1 h-10 bg-black/30 border-white/10 text-white"
                data-testid="flip-force-target-select"
              >
                <SelectValue placeholder="Pick a decoy note…" />
              </SelectTrigger>
              <SelectContent>
                {safeDecoys.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {(d.title && d.title.trim()) || "(untitled)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!flipForceTargetId && (
              <p className="text-[11px] text-amber-300/80 mt-1">
                Pick a target note for the flip to land on.
              </p>
            )}
          </div>

          {flipForceTargetId && (
            <div className="mt-4">
              <Label
                htmlFor="flip-force-target-select-2"
                className="text-white/80 text-sm"
              >
                Target note 2 (kicker — optional)
              </Label>
              <p className="text-[11px] text-white/45 mt-0.5 mb-1 leading-relaxed">
                Second force — fires on the next flip AFTER note 1. Leave
                unset for a single-stage trick. The sequence resets when
                you triple-tap the top strip to exit.
              </p>
              <Select
                value={flipForceTargetId2 || "__none__"}
                onValueChange={(v) =>
                  setFlipForceTargetId2(v === "__none__" ? "" : v)
                }
              >
                <SelectTrigger
                  id="flip-force-target-select-2"
                  className="mt-1 h-10 bg-black/30 border-white/10 text-white"
                  data-testid="flip-force-target-select-2"
                >
                  <SelectValue placeholder="Pick a decoy note…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">(none — single force)</SelectItem>
                  {safeDecoys
                    .filter((d) => d.id !== flipForceTargetId)
                    .map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {(d.title && d.title.trim()) || "(untitled)"}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {flipForceTargetId && flipForceTargetId2 && (
            <div className="mt-4">
              <Label
                htmlFor="flip-force-target-select-3"
                className="text-white/80 text-sm"
              >
                Target note 3 (finale — optional)
              </Label>
              <p className="text-[11px] text-white/45 mt-0.5 mb-1 leading-relaxed">
                Third force — fires on the next flip AFTER note 2. Leave
                unset for a two-stage trick. After all three fire, the
                sequence stops silently until you triple-tap to reset.
              </p>
              <Select
                value={flipForceTargetId3 || "__none__"}
                onValueChange={(v) =>
                  setFlipForceTargetId3(v === "__none__" ? "" : v)
                }
              >
                <SelectTrigger
                  id="flip-force-target-select-3"
                  className="mt-1 h-10 bg-black/30 border-white/10 text-white"
                  data-testid="flip-force-target-select-3"
                >
                  <SelectValue placeholder="Pick a decoy note…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">(none — two-stage trick)</SelectItem>
                  {safeDecoys
                    .filter((d) => d.id !== flipForceTargetId && d.id !== flipForceTargetId2)
                    .map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {(d.title && d.title.trim()) || "(untitled)"}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="mt-4 flex items-center gap-3">
            <Label
              htmlFor="flip-force-dwell-input"
              className="text-white/80 text-sm whitespace-nowrap"
            >
              Min face-down
            </Label>
            <Input
              id="flip-force-dwell-input"
              type="number"
              inputMode="numeric"
              min={500}
              max={10000}
              step={250}
              value={flipForceMinFacedownMs}
              onChange={(e) => {
                const raw = parseInt(e.target.value, 10);
                const clamped = Number.isFinite(raw)
                  ? Math.max(500, Math.min(10000, raw))
                  : 2000;
                setFlipForceMinFacedownMs(clamped);
              }}
              className="w-24 h-10 bg-black/30 border-white/10 text-white text-base tabular-nums"
              data-testid="flip-force-dwell-input"
            />
            <span className="text-sm text-white/55">ms (500–10000)</span>
          </div>

          {flipForceTargetId && (
            <div className="mt-4">
              <Label
                htmlFor="flip-force-body-input"
                className="text-white/80 text-sm"
              >
                Force note 1 body
              </Label>
              <p className="text-[11px] text-white/45 mt-0.5 mb-1.5 leading-relaxed">
                The text shown when the first flip lands. Edits save to
                the target decoy&apos;s body — exactly what the spectator reads.
              </p>
              <Textarea
                id="flip-force-body-input"
                rows={5}
                value={(safeDecoys.find((d) => d.id === flipForceTargetId)?.body) || ""}
                onChange={(e) => {
                  const next = e.target.value;
                  if (typeof setDecoys === "function") {
                    setDecoys((arr) => updateDecoyById(arr, flipForceTargetId, { body: next }));
                  }
                }}
                placeholder="Type what the spectator should see when the force lands…"
                className="bg-black/30 border-white/10 text-white text-sm leading-relaxed"
                data-testid="flip-force-body-input"
              />
            </div>
          )}

          {flipForceTargetId2 && (
            <div className="mt-4">
              <Label
                htmlFor="flip-force-body-input-2"
                className="text-white/80 text-sm"
              >
                Force note 2 body
              </Label>
              <p className="text-[11px] text-white/45 mt-0.5 mb-1.5 leading-relaxed">
                The text shown when the second flip lands.
              </p>
              <Textarea
                id="flip-force-body-input-2"
                rows={5}
                value={(safeDecoys.find((d) => d.id === flipForceTargetId2)?.body) || ""}
                onChange={(e) => {
                  const next = e.target.value;
                  if (typeof setDecoys === "function") {
                    setDecoys((arr) => updateDecoyById(arr, flipForceTargetId2, { body: next }));
                  }
                }}
                placeholder="Type what the spectator should see when the kicker lands…"
                className="bg-black/30 border-white/10 text-white text-sm leading-relaxed"
                data-testid="flip-force-body-input-2"
              />
            </div>
          )}

          {flipForceTargetId3 && (
            <div className="mt-4">
              <Label
                htmlFor="flip-force-body-input-3"
                className="text-white/80 text-sm"
              >
                Force note 3 body
              </Label>
              <p className="text-[11px] text-white/45 mt-0.5 mb-1.5 leading-relaxed">
                The text shown when the third flip lands.
              </p>
              <Textarea
                id="flip-force-body-input-3"
                rows={5}
                value={(safeDecoys.find((d) => d.id === flipForceTargetId3)?.body) || ""}
                onChange={(e) => {
                  const next = e.target.value;
                  if (typeof setDecoys === "function") {
                    setDecoys((arr) => updateDecoyById(arr, flipForceTargetId3, { body: next }));
                  }
                }}
                placeholder="Type what the spectator should see when the finale lands…"
                className="bg-black/30 border-white/10 text-white text-sm leading-relaxed"
                data-testid="flip-force-body-input-3"
              />
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Trident — second decoy. Three numbered lines, reveal in chronological
// order the spectator wrote them (NOT the visual top-to-bottom order).
// ---------------------------------------------------------------------------
export function TridentCard({
  tridentEnabled,
  setTridentEnabled,
  tridentTemplate,
  setTridentTemplate,
  DEFAULT_TRIDENT_TEMPLATE,
}) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="trident-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Wand2 className="w-4 h-4" style={{ color: "#e85a64" }} /> Total Recall (second note)
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            Magician types <span className="text-white/85">1.&nbsp;2.&nbsp;3.</span>{" "}
            and hands the phone over. Spectator writes one word per line
            in any order. Tapping&nbsp;••• reveals the words in the
            chronological order the spectator wrote them.
          </p>
          <p className="text-xs text-white/40 mt-2 leading-relaxed">
            The decoy row title comes from your normal decoy editor
            (<span className="text-white/65">Decoy list &rarr; row 2</span>).
          </p>
        </div>
        <Switch
          checked={tridentEnabled}
          onCheckedChange={setTridentEnabled}
          data-testid="trident-switch"
        />
      </div>

      {tridentEnabled && (
        <div className="mt-5 space-y-5">
          {/* Reveal template */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Reveal template
            </Label>
            <Input
              type="text"
              value={tridentTemplate}
              onChange={(e) => setTridentTemplate(e.target.value)}
              placeholder={DEFAULT_TRIDENT_TEMPLATE}
              className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
              data-testid="trident-template-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Use <span className="text-white/70">(a)</span>{" "}
              <span className="text-white/70">(b)</span>{" "}
              <span className="text-white/70">(c)</span> as placeholders for
              the 1st / 2nd / 3rd word the spectator wrote. Example:{" "}
              <span className="text-white/70">{`You wrote (a) first, then (b), then (c).`}</span>
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

export function TenOutsCard({
  tenOutsEnabled,
  setTenOutsEnabled,
  tenOutsInterval,
  setTenOutsIntervalState,
  tenOutsTemplate,
  setTenOutsTemplate,
  tenOutsWords,
  setTenOutsWordsState,
  MIN_INTERVAL_SEC,
  MAX_INTERVAL_SEC,
}) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="tenouts-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Hash className="w-4 h-4" style={{ color: "#e85a64" }} /> Ten Outs (third note)
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            Preset words cycle silently &mdash; flipping the phone locks
            whichever one is showing into your prediction sentence.
          </p>
        </div>
        <Switch
          checked={tenOutsEnabled}
          onCheckedChange={setTenOutsEnabled}
          data-testid="tenouts-switch"
        />
      </div>

      {tenOutsEnabled && (
        <div className="mt-5 space-y-5">
          {/* Cycle interval slider */}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
                Cycle speed
              </Label>
              <span
                className="text-sm text-white/85 font-medium tabular-nums"
                data-testid="tenouts-interval-readout"
              >
                {tenOutsInterval}s
              </span>
            </div>
            <input
              type="range"
              min={MIN_INTERVAL_SEC}
              max={MAX_INTERVAL_SEC}
              step={1}
              value={tenOutsInterval}
              onChange={(e) =>
                setTenOutsIntervalState(parseInt(e.target.value, 10) || MIN_INTERVAL_SEC)
              }
              className="mt-2 w-full accent-[#c8202c]"
              data-testid="tenouts-interval-slider"
            />
            <div className="flex items-center justify-between text-[10px] text-white/35 mt-1">
              <span>{MIN_INTERVAL_SEC}s</span>
              <span>fast → slow</span>
              <span>{MAX_INTERVAL_SEC}s</span>
            </div>
          </div>

          {/* Prediction template */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Prediction sentence
            </Label>
            <Input
              type="text"
              value={tenOutsTemplate}
              onChange={(e) => setTenOutsTemplate(e.target.value)}
              placeholder='e.g. "I knew you would say (xxx)."'
              className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
              data-testid="tenouts-template-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Put <span className="text-white/70">(xxx)</span> where the locked word should appear.
            </p>
          </div>

          {/* Ten word slots */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Ten outs
            </Label>
            <p className="text-xs text-white/45 mt-1 mb-3 leading-relaxed">
              Fill in up to ten words. The cycle ignores blank slots.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
              {tenOutsWords.map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 text-[11px] text-white/35 text-right tabular-nums">
                    {i + 1}
                  </span>
                  <Input
                    type="text"
                    value={w}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTenOutsWordsState((arr) =>
                        arr.map((x, idx) => (idx === i ? v : x))
                      );
                    }}
                    placeholder={`out ${i + 1}`}
                    className="h-9 bg-black/30 border-white/10 text-white placeholder:text-white/25 text-[14px]"
                    data-testid={`tenouts-word-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Search-Force Prediction — TWO arm modes:
//   • Settings-armed (this card) → static date label + no (card) sub
//   • Lockscreen-armed (camera icon on the fake lockscreen) → LIVE date
//     + (card) placeholder substituted with the freshly-decoded card
// The template lives here and is shared by both flows so magicians only
// author one sentence. (card) collapses to nothing when the trick isn't
// armed via the lockscreen so the sentence still reads correctly.
// ---------------------------------------------------------------------------
export function SearchForceCard({
  searchForceEnabled, setSearchForceEnabled,
  searchForceTitle, setSearchForceTitle,
  searchForceTemplate, setSearchForceTemplate,
  searchForceDateLabel, setSearchForceDateLabel,
  searchForceDateISO, setSearchForceDateISO,
  searchForceDateOffset, setSearchForceDateOffset,
  searchForceQ1Cancel, setSearchForceQ1Cancel,
  searchForceQ1Emergency, setSearchForceQ1Emergency,
  searchForceQ2Cancel, setSearchForceQ2Cancel,
  searchForceQ2Emergency, setSearchForceQ2Emergency,
  searchForceQ3Cancel, setSearchForceQ3Cancel,
  searchForceQ3Emergency, setSearchForceQ3Emergency,
  searchForceQ4Cancel, setSearchForceQ4Cancel,
  searchForceQ4Emergency, setSearchForceQ4Emergency,
  searchForceArmedInfo,
  onClearSearchForceArmed,
  DEFAULT_SEARCH_FORCE_TITLE,
  DEFAULT_SEARCH_FORCE_TEMPLATE,
  DEFAULT_SEARCH_FORCE_DATE_LABEL,
  DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES,
  DEFAULT_SEARCH_FORCE_Q1_CANCEL,
  DEFAULT_SEARCH_FORCE_Q1_EMERGENCY,
  DEFAULT_SEARCH_FORCE_Q2_CANCEL,
  DEFAULT_SEARCH_FORCE_Q2_EMERGENCY,
  DEFAULT_SEARCH_FORCE_Q3_CANCEL,
  DEFAULT_SEARCH_FORCE_Q3_EMERGENCY,
  DEFAULT_SEARCH_FORCE_Q4_CANCEL,
  DEFAULT_SEARCH_FORCE_Q4_EMERGENCY,
}) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="search-force-card"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
            <Search className="w-4 h-4" style={{ color: "#e85a64" }} /> Search-Force Prediction
          </div>
          <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
            While armed, typing anything into the search bar on the
            notes list instantly surfaces a fake &ldquo;old&rdquo;
            prediction note at the top of the results. Also armable
            live from the fake lockscreen (camera quick-action) for a
            dated-NOW variant with a freshly-decoded card.
          </p>
        </div>
        <Switch
          checked={searchForceEnabled}
          onCheckedChange={setSearchForceEnabled}
          data-testid="search-force-switch"
        />
      </div>

      {/* Lockscreen-armed state indicator + clear button */}
      {searchForceArmedInfo && (
        <div
          className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-black/40 border border-white/10 px-4 py-3"
          data-testid="search-force-armed-indicator"
        >
          <div className="text-xs text-white/75 leading-snug">
            <span className="text-white font-medium">Lockscreen-armed:</span>{" "}
            <span data-testid="search-force-armed-card">
              {searchForceArmedInfo.cardLabel}
            </span>
            <div className="text-[10px] text-white/45 mt-0.5">
              (card) placeholder active in body
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="h-8 px-3 text-xs text-white/70 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10"
            onClick={onClearSearchForceArmed}
            data-testid="search-force-clear-armed"
          >
            Disarm
          </Button>
        </div>
      )}

      {searchForceEnabled && (
        <div className="mt-5 space-y-5">
          {/* Title */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Note title
            </Label>
            <Input
              type="text"
              value={searchForceTitle}
              onChange={(e) => setSearchForceTitle(e.target.value)}
              placeholder={DEFAULT_SEARCH_FORCE_TITLE}
              className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-force-title-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Shown at the top of the note and in the search-result row.
            </p>
          </div>

          {/* Metadata date (top of note) — structured datepicker.
              Drives BOTH the header shown at the top of the fake note
              AND the folder-bucket it lives in (Vandaag / Gisteren /
              Vorige 7 dagen / Vorige 30 dagen / Maand Jaar) so both
              stay in sync — the whole point of this trick's illusion. */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Note date (top of note)
            </Label>
            <Input
              type="datetime-local"
              value={(() => {
                // ISO → "YYYY-MM-DDTHH:MM" in LOCAL time (what the
                // datetime-local input expects). Empty when unset.
                if (!searchForceDateISO) return "";
                const d = new Date(searchForceDateISO);
                if (!Number.isFinite(d.getTime())) return "";
                const pad = (n) => String(n).padStart(2, "0");
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
              })()}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) { setSearchForceDateISO(""); return; }
                // Interpret as LOCAL time → ISO. Falls through silently
                // on malformed input so the field stays editable.
                const d = new Date(v);
                if (Number.isFinite(d.getTime())) setSearchForceDateISO(d.toISOString());
              }}
              className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-force-date-iso-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Pick a moment clearly in the past (a few days or weeks
              ago). The prediction note will appear at the top with a
              localized &ldquo;<span className="text-white/70">14 apr om 10:31</span>&rdquo;
              header <em>and</em> lands inside the matching day-group in
              your folder list, so the illusion stays airtight when the
              spectator scrolls back.
            </p>
            {searchForceDateISO ? (
              <button
                type="button"
                onClick={() => setSearchForceDateISO("")}
                className="mt-2 text-[11px] uppercase tracking-[0.28em] text-white/45 hover:text-white/70 transition-colors"
                data-testid="search-force-date-iso-clear"
              >
                Clear &amp; use legacy text label below
              </button>
            ) : null}
          </div>

          {/* Legacy free-form date label — retained ONLY as a fallback
              when the structured picker above is empty. Kept so older
              installs / imported configs don't lose their custom copy. */}
          <div className={searchForceDateISO ? "opacity-40 pointer-events-none" : ""}>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Note date &mdash; legacy free-form fallback
            </Label>
            <Input
              type="text"
              value={searchForceDateLabel}
              onChange={(e) => setSearchForceDateLabel(e.target.value)}
              placeholder={DEFAULT_SEARCH_FORCE_DATE_LABEL}
              className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-force-date-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Only used when the datetime picker above is empty. Free-form
              text like <span className="text-white/70">17 jan 2026 om 08:12</span>.
              Note: with the free-form fallback the folder-bucketing falls
              back to <em>Today</em> because we can&rsquo;t parse arbitrary
              language reliably &mdash; prefer the picker above.
            </p>
          </div>

          {/* (date) offset minutes */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              (date) offset — minutes before now
            </Label>
            <Input
              type="number"
              min={0}
              max={1440}
              value={searchForceDateOffset}
              onChange={(e) => setSearchForceDateOffset(e.target.value)}
              placeholder={String(DEFAULT_SEARCH_FORCE_DATE_OFFSET_MINUTES)}
              className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-force-date-offset-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Number of minutes to backdate the{" "}
              <span className="text-white/70">(date)</span> placeholder
              from the actual moment the spectator opens the note.
              Renders as a localized&nbsp;
              <span className="text-white/70">&ldquo;Today at HH:MM&rdquo;</span>{" "}
              in the app language. 0 = right now. Try 10&ndash;30 min
              for a natural &ldquo;a few minutes ago&rdquo; feel.
            </p>
          </div>

          {/* 4-question personalisation (Cancel / Erase taps) */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              4-question personalisation
            </Label>
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              After tapping the camera icon on the lockscreen, ask four
              binary questions. Tap{" "}
              <span className="text-white/70">Cancel</span> for the
              left-hand answer,{" "}
              <span className="text-white/70">Erase</span> (the keypad
              backspace key) for the right-hand answer — both reachable
              with a single thumb. The four answers substitute{" "}
              <span className="text-white/70">(q1)</span>,{" "}
              <span className="text-white/70">(q2)</span>,{" "}
              <span className="text-white/70">(q3)</span>,{" "}
              <span className="text-white/70">(q4)</span> in the body.
            </p>

            {[
              { i: 1, cancel: searchForceQ1Cancel, setCancel: setSearchForceQ1Cancel,
                emergency: searchForceQ1Emergency, setEmergency: setSearchForceQ1Emergency,
                dc: DEFAULT_SEARCH_FORCE_Q1_CANCEL, de: DEFAULT_SEARCH_FORCE_Q1_EMERGENCY },
              { i: 2, cancel: searchForceQ2Cancel, setCancel: setSearchForceQ2Cancel,
                emergency: searchForceQ2Emergency, setEmergency: setSearchForceQ2Emergency,
                dc: DEFAULT_SEARCH_FORCE_Q2_CANCEL, de: DEFAULT_SEARCH_FORCE_Q2_EMERGENCY },
              { i: 3, cancel: searchForceQ3Cancel, setCancel: setSearchForceQ3Cancel,
                emergency: searchForceQ3Emergency, setEmergency: setSearchForceQ3Emergency,
                dc: DEFAULT_SEARCH_FORCE_Q3_CANCEL, de: DEFAULT_SEARCH_FORCE_Q3_EMERGENCY },
              { i: 4, cancel: searchForceQ4Cancel, setCancel: setSearchForceQ4Cancel,
                emergency: searchForceQ4Emergency, setEmergency: setSearchForceQ4Emergency,
                dc: DEFAULT_SEARCH_FORCE_Q4_CANCEL, de: DEFAULT_SEARCH_FORCE_Q4_EMERGENCY },
            ].map((q) => (
              <div key={q.i} className="mt-3 rounded-xl bg-black/25 border border-white/5 p-3">
                <div className="text-[11px] text-white/55 uppercase tracking-[0.24em] mb-2">
                  Question {q.i} &nbsp;·&nbsp; substitutes (q{q.i})
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                      Cancel tap
                    </div>
                    <Input
                      type="text"
                      value={q.cancel}
                      onChange={(e) => q.setCancel(e.target.value)}
                      placeholder={q.dc}
                      className="h-9 bg-black/40 border-white/10 text-white placeholder:text-white/30 text-sm"
                      data-testid={`search-force-q${q.i}-cancel-input`}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
                      Erase tap
                    </div>
                    <Input
                      type="text"
                      value={q.emergency}
                      onChange={(e) => q.setEmergency(e.target.value)}
                      placeholder={q.de}
                      className="h-9 bg-black/40 border-white/10 text-white placeholder:text-white/30 text-sm"
                      data-testid={`search-force-q${q.i}-emergency-input`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Body template */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
              Prediction body
            </Label>
            <Textarea
              rows={5}
              value={searchForceTemplate}
              onChange={(e) => setSearchForceTemplate(e.target.value)}
              placeholder={DEFAULT_SEARCH_FORCE_TEMPLATE}
              className="mt-3 bg-black/30 border-white/10 text-white placeholder:text-white/30 text-sm leading-relaxed"
              data-testid="search-force-template-input"
            />
            <p className="text-xs text-white/45 mt-2 leading-relaxed">
              Placeholders:{" "}
              <span className="text-white/70">(xxx)</span>=search word ·{" "}
              <span className="text-white/70">(card)</span>=lockscreen card ·{" "}
              <span className="text-white/70">(date)</span>=day &amp; time
              combined ·{" "}
              <span className="text-white/70">(day)</span>=day-and-month
              only ·{" "}
              <span className="text-white/70">(weekday)</span>=weekday
              name ·{" "}
              <span className="text-white/70">(time)</span>=clock only ·{" "}
              <span className="text-white/70">(q1)</span>/
              <span className="text-white/70">(q2)</span>/
              <span className="text-white/70">(q3)</span>/
              <span className="text-white/70">(q4)</span>=the four
              personality answers.
              <br />
              Example:{" "}
              <span className="text-white/70">
                &ldquo;I think on (day) I&rsquo;ll talk to (xxx), it&rsquo;ll
                be around (time). He&rsquo;s a real (q1), prefers (q2),
                chooses (q3) and is more (q4). And thinks of (card).&rdquo;
              </span>
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Add-a-Number — armed per-note by tapping the pencil icon
// ---------------------------------------------------------------------------
export function AddANumberCard({
  addNumForceTotal,
  setAddNumForceTotal,
  addNumSwapPosition,
  setAddNumSwapPosition,
  addNumTrigger,
  setAddNumTrigger,
  DEFAULT_FORCE_TOTAL,
}) {
  return (
    <Card
      className="mt-5 border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl"
      data-testid="addnum-card"
    >
      <div className="flex items-center gap-2 text-white text-base sm:text-lg font-semibold">
        <Calculator className="w-4 h-4" style={{ color: "#e85a64" }} /> Add-a-Number
      </div>
      <p className="text-xs text-white/55 mt-1 max-w-sm leading-relaxed">
        Pencil icon in any note arms the routine. Three numbers from
        the audience always add to your force. Full walkthrough in
        <span className="text-white/75"> The Manual</span>.
      </p>

      <div className="mt-5 space-y-5">
        {/* Force total */}
        <div>
          <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
            Force total
          </Label>
          <Input
            type="text"
            inputMode="numeric"
            value={addNumForceTotal}
            onChange={(e) => setAddNumForceTotal(e.target.value.replace(/[^\d]/g, ""))}
            placeholder={String(DEFAULT_FORCE_TOTAL)}
            className="mt-3 h-10 bg-black/30 border-white/10 text-white placeholder:text-white/30 tabular-nums"
            data-testid="addnum-force-total-input"
          />
          <p className="text-xs text-white/45 mt-2 leading-relaxed">
            The locked-in total the three numbers will always add up to.
            Pre-write this number on a card / in a sealed envelope.
          </p>
        </div>

        {/* Swap position */}
        <div>
          <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
            Number to change
          </Label>
          <Select value={addNumSwapPosition} onValueChange={setAddNumSwapPosition}>
            <SelectTrigger
              className="mt-3 h-10 bg-black/30 border-white/10 text-white"
              data-testid="addnum-swap-position-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1" data-testid="addnum-swap-position-1">
                1.&nbsp;&nbsp;Replace the first number
              </SelectItem>
              <SelectItem value="2" data-testid="addnum-swap-position-2">
                2.&nbsp;&nbsp;Replace the second number
              </SelectItem>
              <SelectItem value="3" data-testid="addnum-swap-position-3">
                3.&nbsp;&nbsp;Replace the third number
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-white/45 mt-2 leading-relaxed">
            Whichever line you pick, the swap stays invisible &mdash;
            spectators who already wrote their number have nothing
            to compare against.
          </p>
        </div>

        {/* Trigger */}
        <div>
          <Label className="text-[11px] uppercase tracking-[0.32em] text-white/55">
            Trigger
          </Label>
          <Select value={addNumTrigger} onValueChange={setAddNumTrigger}>
            <SelectTrigger
              className="mt-3 h-10 bg-black/30 border-white/10 text-white"
              data-testid="addnum-trigger-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="done" data-testid="addnum-trigger-done">
                Keyboard dismiss (Done / Return)
              </SelectItem>
              <SelectItem value="handoff" data-testid="addnum-trigger-handoff">
                Handoff motion (phone passed between hands)
              </SelectItem>
              <SelectItem value="placedown" data-testid="addnum-trigger-placedown">
                Place-down (phone set flat on the table)
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-white/45 mt-2 leading-relaxed">
            What fires the swap. Motion options need iOS permission
            (auto-prompts on first arm).
          </p>
        </div>
      </div>
    </Card>
  );
}
